<script setup lang="ts">
import axios from 'axios'
import dayjs from 'dayjs'
import { ArrowLeft, SquarePen, Trash2, User } from 'lucide-vue-next'
import CustomerLists from './Components/CustomerLists.vue'
import CustomerActivity from './Components/CustomerActivity.vue'
import CustomerActivityFeed from './Components/CustomerActivityFeed.vue'
import CustomerIdentification from './Components/CustomerIdentification.vue'
import CustomerCheckinStats from './Components/CustomerCheckinStats.vue'
import CustomerModal from './Components/CustomerModal.vue'
import CustomerPoints from './Components/CustomerPoints.vue'
import CustomerPointsModal from './Components/CustomerPointsModal.vue'
import CustomerOrders from './Components/CustomerOrders.vue'
import useCustomerDetailTabs from './composables/customerDetailTabs'
import { useElementSize } from '@vueuse/core'

import type { FeedActivity, ActivitiesFeedResponse } from '@/types/customer/ActivitiesFeed'

import {
  AnswerCrm,
  Autopilot,
  Checkin,
  ContestGameParticipation,
  ImportedPoint,
  ManualPoint,
  Message,
  MissedCalls,
  Reward,
  Survey,
  MarketingFlow,
  WebApp
} from '@/types/customer/Activity'

import type { CustomerDetails } from '@/types/types'
import type { ChipColor } from '@/components/base/BaseChip.vue'
import type { HistoryReconstructionStatus } from '@/types/customer/ActivitiesFeed'

const router = useRouter()
const route = useRoute()
const { t } = useI18n()
const {
  checkCustomerFormErrors,
  customerErrors,
  gendersList,
  getDisplayedCustomerName,
  getImagePathFromGenderAndPhone,
  getMerchantAttributes,
  setFormErrors,
  merchantAttributes,
  updateCustomerLists
} = useCustomer()
const { formatDate } = useDate()
const notification = useNotification()
const { permissions, customerTags } = useLocalStorage()
const { locale } = useI18n()
const { send } = useEvent()
const { fetchMerchantAudiences } = useAudience()
const { requestCustomerMerchantUnusedClaimedRewards, requestCustomerMerchantClaimableRewards } = useCustomerRewards()
const { fetchFeed, fetchFeedWithRetries, shouldUseFallback } = useCustomerActivitiesFeed()
const isLoaded = ref(false)
const isActivitiesLoaded = ref(false)
const reconstructionStatus = ref<HistoryReconstructionStatus | null>(null)
const activitiesPage = ref(1)
const isLoadingMoreActivities = ref(false)
const activitiesMeta = ref<{ last_page: number; current_page: number } | null>(null)
const selectedFeedTypes = ref<string[] | null>(null)
const selectedActivityType = ref<string | null>(null)

const leftCol = ref<HTMLElement | null>(null)
const { height: leftColHeight } = useElementSize(leftCol, undefined, { box: 'border-box' })
const isProcessing = ref(false)
const isModalOpen = ref({
  edit: false,
  delete: false
})
const activities = ref([])
const customerPrivateId = ref<string>(route.params.customerId as string)
const customer = ref<CustomerDetails>(null)
const canSeeOrders = computed<boolean | null>(() => (customer.value ? Boolean(customer.value.hasOrders) : null))
const { topTab, topTabs, currentTab, tabs } = useCustomerDetailTabs(canSeeOrders)
const hasOpenedOrders = ref(false)
watch(
  topTab,
  (tab) => {
    if (tab === 'orders') {
      hasOpenedOrders.value = true
    }
  },
  { immediate: true }
)

const monthlyVisitsFrequency = ref(null)

const isPointsModalOpen = ref(false)
const isPointsFormModalOpen = ref(false)

const pointsToSet = ref(null)
const currencyToSet = ref(null)
const isPointsToSetProcessing = ref(false)
const pointsReason = ref('')
const pointsStoreId = ref(null)
const customerLists = ref([])
const allCustomerLists = ref([])
const allStaticCustomerLists = ref([])
const isRewardModalOpen = ref(false)
const customerAllowedRewards = ref(null)
const claimedRewards = ref([])
const rewardIsProcessing = ref({
  claim: false,
  use: false,
  modal: false
})

const customerMerchantAttributes = ref()
const hasNoMerchantAttributes = ref(false)
const selectedAttributes = ref()

const customerEdit = ref()
const customerInitialLists = ref([])
const showCustomerErrors = ref(false)

const avatar = computed(() => getImagePathFromGenderAndPhone(customer.value.gender, customer.value.phone))

const registerDate = computed(() => dayjs(customer.value.registerDate).locale(locale.value).format('LL'))

const title = computed(() => getDisplayedCustomerName(customer.value))

const avatarFallbackInitial = computed(() => {
  const firstChar = (title.value ?? '').trim().charAt(0)
  return /[a-zA-Z]/.test(firstChar) ? firstChar.toUpperCase() : null
})

const showEditUserButton = computed(() => permissions.value['customer.update'])

const calculateMonthlyVisitsFrequency = () => {
  const elapsedMonths = dayjs().diff(dayjs(customer.value.registerDate), 'months')
  monthlyVisitsFrequency.value =
    elapsedMonths <= 1 ? customer.value.checkinCount : (customer.value.checkinCount / elapsedMonths).toFixed(1)
}

const editCustomer = async () => {
  if (isProcessing.value) {
    return
  }
  showCustomerErrors.value = true

  if (!checkCustomerFormErrors(customerEdit.value)) {
    const selectedTags = customerTags.value.filter((tag) => customerEdit.value.tags.includes(tag.id))
    const pointsToAddOrRemove = customerEdit.value.points - customer.value.points
    const pointsType = pointsToAddOrRemove < 0 ? 'debit' : 'credit'

    const requestParams = {
      phoneNumber: customerEdit.value.phone?.replace(/\s/g, ''),
      points: pointsToAddOrRemove ? Math.abs(pointsToAddOrRemove) : null,
      referenceStoreId: customerEdit.value.referenceStore.id,
      type: pointsToAddOrRemove ? pointsType : null,
      country: customerEdit.value.country ?? 'FR',
      postalCode: customerEdit.value.postalCode,
      city: customerEdit.value.city,
      firstName: customerEdit.value.firstname,
      lastName: customerEdit.value.lastname,
      email: customerEdit.value.email,
      loyaltyCardNumber: customerEdit.value.loyaltyCardNumber ?? null,
      gender: customerEdit.value.gender
        ? gendersList.find((gender) => gender.id === customerEdit.value.gender).slug
        : null,
      birthday: customerEdit.value.birthdayFormatted,
      hasOptin: customerEdit.value.optin,
      hasOptinEmail: customerEdit.value.hasOptinEmail,
      tags: selectedTags.map((tag) => {
        return tag.slug
      })
    }

    isProcessing.value = true

    try {
      await axios.put(`/customers/${customerPrivateId.value}`, {
        ...requestParams
      })

      await Promise.all([saveOptin(), saveAttributes()])

      const customerSegments = [
        ...(customerEdit?.value?.lists ? customerEdit.value.lists.map((segment) => segment.id) : []),
        ...(customerInitialLists?.value
          ? customerInitialLists.value.filter((segment) => segment.type === 'dynamic').map((segment) => segment.id)
          : [])
      ]
      await updateCustomerLists(parseInt(route?.params?.customerId as string), customerSegments)
      isModalOpen.value.edit = false
      isLoaded.value = false
      notification.create('success', {
        text: t('pages.customer.customerPage.notifications.edit.success'),
        timeout: 3000
      })
      const audiences = await fetchMerchantAudiences()
      allCustomerLists.value = audiences.data

      await getCustomerLists()
      populateCustomerEdition()
      fetchData()
    } catch (error) {
      if (error?.response?.data) setFormErrors(error.response.data)
    } finally {
      isProcessing.value = false
    }
  }
}

const saveAttributes = async () => {
  if (hasNoMerchantAttributes.value) {
    return
  }
  const requests = []

  for (const item of customerMerchantAttributes.value) {
    const id = item.id

    if (Object.prototype.hasOwnProperty.call(selectedAttributes.value, id)) {
      const value = selectedAttributes.value[id]

      if (!value && item.itemId) {
        requests.push(axios.delete(`customers/${customerPrivateId.value}/attributes/${item.itemId}`))
      } else if (value && !item.itemId) {
        requests.push(
          axios.post(`customers/${customerPrivateId.value}/attributes`, {
            attributeId: item.id,
            value: value
          })
        )
      } else if (value && item.itemId) {
        requests.push(
          axios.patch(`customers/${customerPrivateId.value}/attributes/${item.itemId}`, {
            value: value
          })
        )
      }
    }
  }

  await Promise.all(requests)
}

const saveOptin = async () => {
  if (customerEdit.value.optin !== customer.value.hasOptin) {
    await axios.patch(`/customers/${customerPrivateId.value}/optin`, {
      hasOptin: customerEdit.value.optin
    })
  }
}

const deleteCustomer = async () => {
  if (isProcessing.value) {
    return
  }
  isProcessing.value = true

  try {
    await axios.delete(`/customers/${customerPrivateId.value}`)
    notification.create('success', {
      text: t('pages.customer.customerDetailPage.deleteCustomer.success')
    })
    router.push({ name: 'CustomerList' })
  } catch {
    notification.create('error', {
      text: t('pages.customer.customerDetailPage.deleteCustomer.error')
    })
  } finally {
    isProcessing.value = false
  }
}

const requestCustomerCampaignMessages = async () => {
  let currentPage = 1
  let hasMorePages = true

  while (hasMorePages) {
    const { data } = await axios.get(`customers/${customerPrivateId.value}/messages/campaigns`, {
      params: {
        page: currentPage
      }
    })

    if (!data?.data) {
      break
    }

    const newActivities = data.data.map((campaignMessage) => {
      const activity = new Message()
      activity.date = new Date(campaignMessage.sentAt)
      activity.content = campaignMessage.content
      activity.messageChannel = campaignMessage.messageChannel
      activity.convertedAt = campaignMessage.convertedAt ? new Date(campaignMessage.convertedAt) : null
      activity.sentAt = campaignMessage.sentAt

      return activity
    })

    activities.value.push(...newActivities)

    hasMorePages = data.meta?.last_page ? currentPage < data.meta.last_page : false
    currentPage++
  }
}

const requestCustomer = async () => {
  try {
    const { data } = await axios.get(`customers/${customerPrivateId.value}`)
    customer.value = data.data
    customer.value.points = customer.value.points ?? 0
    calculateMonthlyVisitsFrequency()
    populateCustomerEdition()
  } catch (error) {
    if (error.response.status === 404) {
      return router.push({
        name: 'CustomerList'
      })
    }
  }
}

const populateCustomerEdition = () => {
  const { country, referenceStore, hasOptin, firstName, lastName, birthDate, gender, tags } = customer.value

  customerEdit.value = {
    ...customer.value,
    country: country.alpha2Code,
    referenceStore: { ...referenceStore },
    optin: hasOptin,
    firstname: firstName,
    lastname: lastName,
    birthday: birthDate ? formatDate({ date: birthDate, toFormat: 'YYYY-MM-DD' }) : null,
    gender: gendersList.find((genderObj) => genderObj.slug === gender).id,
    tags: tags.map((tag) => tag.id),
    lists: customerLists.value
  }

  customerInitialLists.value = customerLists.value
}

const requestImportedPoints = async () => {
  const { data } = await axios.get(`customers/${customerPrivateId.value}/points/imported`)
  data.data.forEach((point) => {
    const activity = new ImportedPoint()
    activity.date = new Date(point.date)
    activity.points = point.points
    activities.value.push(activity)
  })
}

const requestCheckIns = async () => {
  const { data } = await axios.get(`/customers/${customerPrivateId.value}/checkins`, {
    params: {
      pageSize: 1000,
      page: 1
    }
  })
  data.data.forEach((checkin) => {
    const activity = new Checkin()
    activity.date = new Date(checkin.date)
    activity.points = checkin.points
    activity.store = checkin?.store?.name ?? t('pages.customer.customerDetailPage.activities.noStore')
    activities.value.push(activity)
  })
}

const requestContestGameParticipation = async () => {
  const { data } = await axios.get(`/contest-games/participants/${customerPrivateId.value}`)
  data.data.forEach((participation) => {
    const activity = new ContestGameParticipation()
    activity.date = new Date(participation.participatedAt)
    activity.win = participation.contestGameReward !== null
    activity.reward = participation.contestGameReward?.name
    activity.store = participation.store.name
    activities.value.push(activity)
  })
}

const requestReward = async () => {
  const { data } = await axios.get(`/customers/${customerPrivateId.value}/activities/rewards`)
  return data.data.forEach((claim) => {
    const activity = new Reward()
    activity.date = new Date(claim.date)
    activity.text = claim.reward?.name ?? ''
    activities.value.push(activity)
  })
}

const requestMarketingFlows = async () => {
  const { data } = await axios.get(`/customers/${customerPrivateId.value}/activities/marketing-flows`)
  data.data.forEach((marketingFlow) => {
    const activity = new MarketingFlow()
    activity.date = new Date(marketingFlow.date)
    activity.points = marketingFlow.value
    activity.marketingFlow = marketingFlow.actionable
    activities.value.push(activity)
  })
}

const requestManualPoint = async () => {
  const { data } = await axios.get(`/customers/${customerPrivateId.value}/points/manual`)
  return data.data.forEach((manual) => {
    const activity = new ManualPoint()
    activity.date = new Date(manual.date)
    activity.points = manual.points
    activities.value.push(activity)
  })
}

const requestSurvey = async () => {
  const { data } = await axios.get('/survey-answers', {
    params: {
      customerIds: customerPrivateId.value
    }
  })
  return data.data.forEach((survey) => {
    const activity = new Survey()
    if (survey.choice !== null) {
      activity.choice = survey.choice.text
    } else {
      activity.choice = null
    }
    activity.question = survey.surveyQuestion.questionText
    activity.date = new Date(survey.date)
    activities.value.push(activity)
  })
}

const requestAnswersCrm = async () => {
  const { data } = await axios.get('/enrichment-questions/answers', {
    params: {
      customerId: customerPrivateId.value
    }
  })
  return data.data.forEach((answer) => {
    const activity = new AnswerCrm()
    activity.date = new Date(answer.date)
    activity.question = t(`pages.crm.crmPage.blocks.drag.questions.${answer.question.slug}`)
    if (answer.text !== null) {
      activity.question = activity.question.toLowerCase()
    }
    switch (answer.question.slug) {
      case 'birthday':
        activity.answer = answer.text ?? null
        break
      case 'gender':
        activity.answer = answer.text === null ? null : t(`global.gender.${answer.text}`)
        break
      default:
        activity.answer = answer.text
    }
    activities.value.push(activity)
  })
}

const requestMissedCalls = async () => {
  const { data } = await axios.get(`/customers/${customerPrivateId.value}/missed-calls`)
  return data.data.forEach((missedCalls) => {
    const activity = new MissedCalls()
    activity.store = missedCalls.store
    activity.date = new Date(missedCalls.calledAt)
    activity.isClicked = missedCalls.isClicked
    activities.value.push(activity)
  })
}

const requestAutopilot = async () => {
  const { data } = await axios.get(`/customers/${customerPrivateId.value}/autopilots`)
  return data.data.forEach((autopilot) => {
    if (autopilot.isConverted) {
      const activity = new Autopilot()

      activity.points = autopilot.points
      activity.message = autopilot.message
      activity.date = new Date(autopilot.sentAt)
      activity.convertedAt = new Date(autopilot.convertedAt)

      activities.value.push(activity)
    } else {
      const activity = new Message()

      activity.convertedAt = null
      activity.sentAt = autopilot.sentAt
      activity.date = new Date(autopilot.sentAt)
      activity.content = autopilot.message
      activity.messageChannel = autopilot.messageChannel

      activities.value.push(activity)
    }
  })
}

const requestWebApp = async () => {
  const { data } = await axios.get(`customers/${customerPrivateId.value}/webapp`)
  data.data.forEach((webAppCheckin) => {
    const activity = new WebApp()
    activity.date = new Date(webAppCheckin.date)
    activity.points = webAppCheckin.points
    activity.store = webAppCheckin?.store ?? t('pages.customer.customerDetailPage.activities.noStore')
    activity.type = webAppCheckin.type
    activities.value.push(activity)
  })
}

const refreshActivitiesAfterChange = async () => {
  if (!customerPrivateId.value) {
    return
  }

  activitiesPage.value = 1
  activitiesMeta.value = null
  isActivitiesLoaded.value = false

  if (useLegacyActivities.value) {
    activities.value = []
    await fetchActivitiesLegacy(selectedActivityType.value)
  } else {
    feedActivities.value = []
    await fetchActivitiesFeed(1, false)
  }

  isActivitiesLoaded.value = true
}

const handleSaveNewTag = async (tag) => {
  await axios.post(`customers/${customerPrivateId.value}/tags`, {
    tagIds: [tag]
  })
  await requestCustomer()
  await refreshActivitiesAfterChange()
}

const handleClaimReward = async () => {
  await requestCustomer()
  await refreshActivitiesAfterChange()
}

const setBadgeColor = (attribute): ChipColor => {
  const colorMap = {
    VIP: 'yellow',
    NEW: 'blue',
    OCCASIONAL: 'gray',
    AT_RISK: 'red',
    true: 'green',
    false: 'red',
    active: 'green',
    deleted: 'red',
    downloaded: 'orange',
    null: 'gray'
  }

  return colorMap[attribute] || 'gray'
}
const setTooltip = (value, attribute) => {
  const val = value === undefined || value === null ? 'null' : value.toString()
  return t(`pages.customer.customerDetailPage.tooltip.${val}`, {
    format: t(`pages.customer.customerDetailPage.${attribute}`)
  })
}

const fetchActivitiesLegacy = async (filterType: string | null = null) => {
  activities.value = []

  await Promise.all([
    requestAnswersCrm(),
    requestAutopilot(),
    requestCheckIns(),
    requestContestGameParticipation(),
    requestCustomerCampaignMessages(),
    requestImportedPoints(),
    requestManualPoint(),
    requestMissedCalls(),
    requestReward(),
    requestSurvey(),
    requestMarketingFlows(),
    requestWebApp()
  ])

  if (filterType) {
    activities.value = activities.value.filter((activity) => activity.getType() === filterType)
  }
}

const feedActivities = ref<FeedActivity[]>([])

const fetchActivitiesFeed = async (
  page: number = 1,
  append: boolean = false,
  cachedResponse?: { response: ActivitiesFeedResponse; reconstructionStatus: HistoryReconstructionStatus | null }
) => {
  if (!customerPrivateId.value) {
    return
  }

  let result: { response: ActivitiesFeedResponse; reconstructionStatus: HistoryReconstructionStatus | null }

  if (cachedResponse && page === 1 && !append) {
    result = cachedResponse
  } else {
    result = await fetchFeed(customerPrivateId.value, {
      pageSize: 20,
      page,
      type: selectedFeedTypes.value || undefined
    })
  }

  reconstructionStatus.value = result.reconstructionStatus

  activitiesMeta.value = {
    last_page: result.response.meta.last_page,
    current_page: result.response.meta.current_page
  }

  if (append) {
    feedActivities.value = [...feedActivities.value, ...result.response.data]
  } else {
    feedActivities.value = result.response.data
  }
}

const loadMoreActivities = async () => {
  if (
    isLoadingMoreActivities.value ||
    !activitiesMeta.value ||
    activitiesPage.value >= activitiesMeta.value.last_page ||
    useLegacyActivities.value
  ) {
    return
  }

  isLoadingMoreActivities.value = true
  try {
    activitiesPage.value++
    await fetchActivitiesFeed(activitiesPage.value, true)
  } finally {
    isLoadingMoreActivities.value = false
  }
}

const handleFeedTypeFilterChange = async (feedTypes: string[] | null) => {
  isActivitiesLoaded.value = false
  activitiesPage.value = 1
  activitiesMeta.value = null

  selectedFeedTypes.value = feedTypes
  feedActivities.value = []
  await fetchActivitiesFeed(1, false)

  isActivitiesLoaded.value = true
}

const handleActivityTypeFilterChange = async (activityType: string | null) => {
  isActivitiesLoaded.value = false
  activitiesPage.value = 1
  activitiesMeta.value = null

  selectedActivityType.value = activityType
  activities.value = []
  await fetchActivitiesLegacy(activityType)

  isActivitiesLoaded.value = true
}

const useLegacyActivities = ref(false)

const fetchData = async (type?: 'claimedRewards' | 'init') => {
  activities.value = []
  feedActivities.value = []
  reconstructionStatus.value = null
  isActivitiesLoaded.value = false
  activitiesPage.value = 1
  activitiesMeta.value = null
  selectedFeedTypes.value = null
  selectedActivityType.value = null

  let callsToMakeToGetPage = [requestCustomer()]
  switch (type) {
    case 'init':
      callsToMakeToGetPage.push(getCustomerMerchantAttributes())
      break
    case 'claimedRewards':
      callsToMakeToGetPage.push(requestCustomerMerchantUnusedClaimedRewards(customerPrivateId.value))
      break
  }

  const loadLegacyActivities = async () => {
    await Promise.all([
      requestAnswersCrm(),
      requestAutopilot(),
      requestCheckIns(),
      requestContestGameParticipation(),
      requestCustomerCampaignMessages(),
      requestImportedPoints(),
      requestManualPoint(),
      requestMissedCalls(),
      requestReward(),
      requestSurvey(),
      requestMarketingFlows(),
      requestWebApp()
    ])
  }

  try {
    await Promise.all(callsToMakeToGetPage)
    isLoaded.value = true
    isActivitiesLoaded.value = false

    try {
      const feedResult = await fetchFeedWithRetries(
        customerPrivateId.value,
        {
          pageSize: 20,
          page: 1
        },
        (successResult) => {
          useLegacyActivities.value = false
          feedActivities.value = []
          fetchActivitiesFeed(1, false, successResult)
        }
      )

      if (shouldUseFallback(feedResult.response.data, feedResult.reconstructionStatus)) {
        useLegacyActivities.value = true
        await loadLegacyActivities()
      } else {
        useLegacyActivities.value = false
        await fetchActivitiesFeed(1, false, feedResult)
      }
    } catch {
      useLegacyActivities.value = true
      await loadLegacyActivities()
    }
  } finally {
    isLoaded.value = true
    isActivitiesLoaded.value = true
  }
}

const handleConfirmSetPoints = (reason: string, storeId: number) => {
  isPointsModalOpen.value = true
  pointsReason.value = pointsToSet.value < 0 ? 'manual' : reason
  pointsStoreId.value = pointsToSet.value < 0 ? null : storeId
}

const handleSetPoints = async () => {
  if (isPointsToSetProcessing.value) {
    return
  }
  isPointsToSetProcessing.value = true
  try {
    await axios.patch(`/customers/${customerPrivateId.value}/points`, {
      points: Math.abs(pointsToSet.value),
      type: pointsToSet.value < 0 ? 'debit' : 'credit',
      reason: pointsReason.value,
      storeId: pointsStoreId.value
    })
    await fetchData()
    notification.create('success', {
      text: t('pages.customer.customerDetailPage.notifications.points.success.true'),
      timeout: 3000
    })
    isPointsModalOpen.value = false
    isPointsFormModalOpen.value = false
  } catch {
    notification.create('error', {
      text: t('pages.customer.customerDetailPage.notifications.points.success.false')
    })
  } finally {
    isPointsToSetProcessing.value = false
    pointsToSet.value = null
    currencyToSet.value = null
  }
}

const openRewardModal = async () => {
  if (rewardIsProcessing.value.modal) {
    return
  }

  rewardIsProcessing.value.modal = true
  claimedRewards.value = await requestCustomerMerchantUnusedClaimedRewards(customerPrivateId.value)
  if (customerAllowedRewards.value) {
    isRewardModalOpen.value = true
    rewardIsProcessing.value.modal = false
    return
  }
  try {
    customerAllowedRewards.value = await requestCustomerMerchantClaimableRewards(customerPrivateId.value)

    isRewardModalOpen.value = true
    rewardIsProcessing.value.modal = false
  } catch {
    notification.create('error', {
      text: t('pages.customer.customerDetailPage.notifications.points.success.false')
    })
  }
}

const openPointsFormModal = () => {
  if (isPointsFormModalOpen.value) {
    return
  }
  isPointsFormModalOpen.value = true
}

const closeRewardModal = () => {
  isRewardModalOpen.value = false
  router.replace({ query: { ...route.query, isRewardModalOpen: undefined } })
}

const getCustomerMerchantAttributes = async () => {
  customerMerchantAttributes.value = []
  if (merchantAttributes.value.length < 1) {
    await getMerchantAttributes()
  }

  hasNoMerchantAttributes.value = merchantAttributes.value.length < 1

  if (hasNoMerchantAttributes.value) {
    return
  }

  const { data } = await axios.get(`/customers/${customerPrivateId.value}/attributes`)

  merchantAttributes.value.forEach((attribute) => {
    const findCustomerMerchantAttribute = data.data.find(
      (customerMerchantAttributes) => customerMerchantAttributes.attributeValue.attribute.id === attribute.id
    )

    selectedAttributes.value = {
      ...selectedAttributes.value,
      [attribute.id]: findCustomerMerchantAttribute ? findCustomerMerchantAttribute.attributeValue.value : null
    }

    const customerAttributesWithIdToUpdate = { ...attribute, itemId: findCustomerMerchantAttribute?.id }
    customerMerchantAttributes.value.push(customerAttributesWithIdToUpdate)
  })
}

const getCustomerPrivateId = async () => {
  if (
    !route.params.customerId.toString().startsWith('cust_') &&
    !/^[a-f0-9]+[a-z]+[a-z0-9]+$/.test(route.params.customerId.toString())
  ) {
    return
  }
  try {
    const { data } = await axios.get(`/customers/public/${route.params.customerId}`)
    customerPrivateId.value = data.data.id
  } catch {
    router.go(-1)
  }
}

const getCustomerLists = async () => {
  const { data } = await axios.get(`/customers/${customerPrivateId.value}/segments`)
  customerLists.value = data.data
}
const currentSegmentsPage = ref(1)
const lastSegmentsPage = ref(null)

const handleLoadAudiences = async (text) => {
  currentSegmentsPage.value++
  if (currentSegmentsPage.value > lastSegmentsPage.value) {
    return
  }
  if (text && text !== '') {
    const audiences = await fetchMerchantAudiences({
      pagination: {
        pageSize: 15,
        page: currentSegmentsPage.value
      },
      search: text,
      type: 'static'
    })
    allCustomerLists.value = audiences.data
    allStaticCustomerLists.value = audiences.data.filter((audience) => audience.type === 'static')

    lastSegmentsPage.value = audiences.meta.last_page
  }
}

const handleEditCustomer = () => {
  isModalOpen.value.edit = true
  send('segment', 'customer:update:clicked')
}

const handleSearchAudience = async (text) => {
  const searchedAudiences = await fetchMerchantAudiences({ search: text, type: 'static' })
  allCustomerLists.value = searchedAudiences.data
}

onMounted(async () => {
  await getCustomerPrivateId()
  await getCustomerLists()
  const audiences = await fetchMerchantAudiences({ pagination: { pageSize: 1000, page: 1 } })
  allCustomerLists.value = audiences.data
  allStaticCustomerLists.value = audiences.data.filter((audience) => audience.type === 'static')
  fetchData('init')
  if (route.query.isRewardModalOpen) {
    openRewardModal()
  }
})
</script>

<template>
  <div>
    <div v-if="isLoaded">
      <div class="z-50 px-6 pb-40 pt-6 sm:px-12 md:pb-6">
        <router-link
          :to="{ name: 'CustomerList' }"
          class="mb-2 inline-flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground"
        >
          <ArrowLeft class="size-4" />
          {{ t('pages.customer.customerPage.title') }}
        </router-link>
        <div class="mb-6">
          <div class="flex flex-col items-end md:flex-row md:justify-between">
            <div class="flex w-full flex-col">
              <div class="flex items-center gap-4">
                <div class="relative">
                  <Avatar class="size-20">
                    <AvatarImage :src="avatar" alt="" />
                    <AvatarFallback>
                      <span v-if="avatarFallbackInitial" class="text-xl">{{ avatarFallbackInitial }}</span>
                      <User v-else class="h-8 w-8 text-muted-foreground" />
                    </AvatarFallback>
                  </Avatar>
                  <span
                    class="absolute bottom-0 right-0 hidden h-6 w-6 rounded-full bg-cover bg-center ring-2 ring-white sm:block"
                    :style="{ backgroundImage: `url(${customer.countryFlag})` }"
                  ></span>
                </div>
                <div class="flex flex-col gap-2">
                  <div class="flex flex-col gap-4 md:flex-row md:items-center">
                    <span class="text-ellipsis align-middle text-2xl font-bold capitalize text-foreground">
                      {{ title }}
                    </span>
                    <div class="inline-flex gap-4">
                      <Tooltip>
                        <TooltipTrigger as-child>
                          <div>
                            <BaseChip
                              :color="setBadgeColor(customer.hasOptin)"
                              icon="HeroChatBubbleLeftRightIconSolid"
                              :text="t('pages.customer.customerDetailPage.sms')"
                            />
                          </div>
                        </TooltipTrigger>
                        <TooltipContent side="right">{{ setTooltip(customer.hasOptin, 'sms') }}</TooltipContent>
                      </Tooltip>

                      <Tooltip>
                        <TooltipTrigger as-child>
                          <div>
                            <BaseChip
                              :color="setBadgeColor(customer.hasOptinEmail)"
                              icon="HeroEnvelopeIconSolid"
                              :text="t('pages.customer.customerDetailPage.email')"
                            />
                          </div>
                        </TooltipTrigger>
                        <TooltipContent side="right">{{ setTooltip(customer.hasOptinEmail, 'email') }}</TooltipContent>
                      </Tooltip>

                      <Tooltip v-if="customer.walletExternalId">
                        <TooltipTrigger as-child>
                          <div>
                            <BaseChip
                              :color="setBadgeColor(customer.walletStatus)"
                              icon="HeroBellIconSolid"
                              :text="t('pages.customer.customerDetailPage.wallet.push')"
                            />
                          </div>
                        </TooltipTrigger>
                        <TooltipContent side="right">
                          {{ setTooltip(customer.walletStatus, 'notificationPush') }}
                        </TooltipContent>
                      </Tooltip>
                    </div>
                  </div>
                  <p class="text-sm font-medium text-muted-foreground">
                    {{
                      t(
                        `pages.customer.customerDetailPage.customerDetail.${['man', 'woman'].includes(customer.gender) ? customer.gender : 'unknown'}`,
                        {
                          registerDate: registerDate
                        }
                      )
                    }}
                  </p>
                </div>
              </div>
            </div>
            <div class="flex w-full gap-2 sm:w-auto">
              <Button v-if="showEditUserButton" variant="outline" class="w-full sm:w-auto" @click="handleEditCustomer">
                <SquarePen />
                <span>{{ t('pages.customer.customerDetailPage.buttons.edit') }}</span>
              </Button>
              <Button variant="destructive" class="w-full sm:w-auto" @click="isModalOpen.delete = true">
                <Trash2 />
                <span class="hidden sm:flex">{{ t('pages.customer.customerDetailPage.buttons.delete') }}</span>
              </Button>
            </div>
          </div>
        </div>

        <BaseModal
          :is-open="isModalOpen.delete"
          :is-processing="isProcessing"
          :link-text="t('pages.customer.customerDetailPage.buttons.deleteCustomer')"
          :text-title="t('pages.customer.customerDetailPage.deleteCustomer.confirm.title')"
          :text="t('pages.customer.customerDetailPage.deleteCustomer.confirm.description')"
          :cancel="t('ui.confirmModal.buttons.cancel')"
          icon="HeroExclamationTriangleIconSolid"
          icon-color="red"
          @click="deleteCustomer"
          @close="isModalOpen.delete = false"
        />
        <BaseModal
          :heading-title="t('pages.customer.customerPage.blocks.edit.description')"
          :is-open="isModalOpen.edit"
          :is-processing="isProcessing"
          :link-text="t('global.words.save')"
          size="3xl"
          @click="editCustomer"
          @close="isModalOpen.edit = false"
        >
          <CustomerModal
            v-model:new-customer="customerEdit"
            v-model:selected-attributes="selectedAttributes"
            v-model:errors="customerErrors"
            :is-creation-mode="false"
            :alpha2-code="customer.country.alpha2Code"
            :genders-list="gendersList"
            :attributes="merchantAttributes"
            :customer-lists="allStaticCustomerLists"
            @search-audience="handleSearchAudience($event)"
            @load-more-audiences="handleLoadAudiences($event)"
          />
        </BaseModal>

        <div v-if="canSeeOrders" class="mb-6 border-b">
          <BaseTab v-model="topTab" :tabs="topTabs" />
        </div>

        <CustomerOrders
          v-if="hasOpenedOrders && canSeeOrders"
          v-show="topTab === 'orders'"
          :customer-id="customerPrivateId"
        />

        <div v-show="topTab === 'profile'">
          <div class="my-3 mb-0 border-b block md:hidden">
            <BaseTab v-model="currentTab" :tabs="tabs" disable-router />
          </div>

          <div class="mt-4 sm:mb-10 sm:mt-0">
            <CustomerPoints
              v-model:points-to-set="pointsToSet"
              v-model:currency-to-set="currencyToSet"
              :customer="customer"
              :reward-is-processing="rewardIsProcessing"
              :is-reward-modal-open="isRewardModalOpen"
              :is-points-form-modal-open="isPointsFormModalOpen"
              :is-points-to-set-processing="isPointsToSetProcessing"
              :rewards="customerAllowedRewards"
              :display-mobile-loyalty="currentTab === 'informations'"
              @on-open-reward-modal="openRewardModal"
              @on-close-reward-modal="closeRewardModal"
              @on-claim-reward="handleClaimReward"
              @on-close-points-form-modal="isPointsFormModalOpen = false"
              @on-open-points-form-modal="openPointsFormModal"
              @on-set-points="handleConfirmSetPoints"
            />

            <CustomerPointsModal
              :is-processing="isPointsToSetProcessing"
              :is-points-modal-open="isPointsModalOpen"
              :points-to-set="pointsToSet"
              :current-points="customer.points"
              @on-close="isPointsModalOpen = false"
              @on-confirm="handleSetPoints"
            />
          </div>

          <div class="flex flex-col sm:gap-6 lg:flex-row">
            <div class="w-full sm:space-y-6 lg:w-7/12">
              <div id="informations" class="sm:block" :class="{ hidden: currentTab !== 'informations' }">
                <section
                  ref="leftCol"
                  aria-labelledby="applicant-information-title"
                  class="flex flex-col space-y-6 sm:h-auto"
                >
                  <CustomerIdentification
                    :customer="customer"
                    :customer-attributes="selectedAttributes"
                    :merchant-attributes="merchantAttributes"
                    @on-save-tag="handleSaveNewTag"
                  />
                  <CustomerLists :customer-lists="customerLists" />
                  <CustomerCheckinStats
                    :customer="customer"
                    :monthly-visits-frequency="Number(monthlyVisitsFrequency)"
                  />
                </section>
              </div>
            </div>
            <CustomerActivity
              v-if="leftCol && useLegacyActivities"
              id="activities"
              class="sm:block"
              :is-loaded="isActivitiesLoaded"
              :class="{ hidden: currentTab !== 'activities' }"
              :activities="activities"
              :height="leftColHeight"
              @filter-change="handleActivityTypeFilterChange"
            />
            <CustomerActivityFeed
              v-else-if="leftCol"
              id="activities"
              class="sm:block"
              :is-loaded="isActivitiesLoaded"
              :class="{ hidden: currentTab !== 'activities' }"
              :activities="feedActivities"
              :height="leftColHeight"
              :reconstruction-status="reconstructionStatus"
              :has-more="activitiesMeta ? activitiesPage < activitiesMeta.last_page : false"
              :is-loading-more="isLoadingMoreActivities"
              @load-more="loadMoreActivities"
              @filter-change="handleFeedTypeFilterChange"
            />
          </div>
        </div>
      </div>
    </div>
    <TheLoadingPage v-else page="CustomerDetail" />
  </div>
</template>
